import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

import { useAlert } from '../context/AlertContext';
import {
  useEndReadingSession,
  useGetReadingAnnotations,
  useUpdateReadingSession,
} from '../hooks/reactQuerys/TabletReadingQueries';
import { ITabletReadingSession } from '../Interfaces/ITabletReadingSession';
import { PdfDocument } from '../Document/PdfDocument';
import { fetchDataStream } from '../hooks/useFetch';

const ONLINE_THRESHOLD_MINUTES = 2;
function isOnline(lastSeen?: string | null): boolean {
  if (!lastSeen) return false;
  const ts = new Date(lastSeen).getTime();
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts <= ONLINE_THRESHOLD_MINUTES * 60_000;
}

interface Props {
  session: ITabletReadingSession;
}

export const ActiveReadingSessionPanel: React.FC<Props> = ({ session }) => {
  const { showAlert } = useAlert();
  const { updateReadingSession, isPendingUpdateReadingSession } =
    useUpdateReadingSession();
  const { endReadingSession, isPendingEndReadingSession } =
    useEndReadingSession();
  const { annotations } = useGetReadingAnnotations(
    session.idTabletReadingSession
  );

  const [pageInput, setPageInput] = React.useState<string>(
    String(session.CurrentPage ?? 1)
  );
  React.useEffect(() => {
    setPageInput(String(session.CurrentPage ?? 1));
  }, [session.CurrentPage]);

  const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = React.useState(true);
  const [numPages, setNumPages] = React.useState(1);
  const [showDocument, setShowDocument] = React.useState(true);

  React.useEffect(() => {
    let objectUrl: string | null = null;
    setPdfLoading(true);
    fetchDataStream({
      endpoint: `/tabletreadingsession/${session.idTabletReadingSession}/document`,
    })
      .then(blob => {
        if (blob instanceof Blob) {
          objectUrl = URL.createObjectURL(blob);
          setPdfUrl(objectUrl);
        }
      })
      .catch(() => {})
      .finally(() => setPdfLoading(false));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [session.idTabletReadingSession]);

  async function setPage(next: number) {
    if (next < 1) return;
    try {
      await updateReadingSession({
        idTabletReadingSession: session.idTabletReadingSession,
        body: { CurrentPage: next },
      });
    } catch {
      showAlert({
        text: 'Seite konnte nicht gepusht werden.',
        severity: 'error',
      });
    }
  }

  async function toggleControlMode() {
    const nextMode = session.ControlMode === 'push' ? 'free' : 'push';
    try {
      await updateReadingSession({
        idTabletReadingSession: session.idTabletReadingSession,
        body: { ControlMode: nextMode },
      });
    } catch {
      showAlert({
        text: 'Steuerungsmodus konnte nicht gewechselt werden.',
        severity: 'error',
      });
    }
  }

  async function handleEnd() {
    if (
      !window.confirm(
        'Mitlese-Sitzung wirklich beenden? Anmerkungen werden als Entwurfs-Anmerkungen in den Vorgang übernommen.'
      )
    )
      return;
    try {
      const result = (await endReadingSession(
        session.idTabletReadingSession
      )) as { transferredAnnotations?: number } | undefined;
      const n = result?.transferredAnnotations ?? 0;
      if (n > 0) {
        showAlert({
          text: `${n} Anmerkung${n === 1 ? '' : 'en'} wurde${
            n === 1 ? '' : 'n'
          } als Entwurfs-Anmerkung${n === 1 ? '' : 'en'} übernommen.`,
          severity: 'success',
        });
      } else {
        showAlert({
          text: 'Mitlese-Sitzung beendet.',
          severity: 'info',
        });
      }
    } catch {
      showAlert({ text: 'Beenden fehlgeschlagen.', severity: 'error' });
    }
  }

  const devices = session.devices ?? [];

  return (
    <Box
      sx={{
        border: theme => `2px solid ${theme.palette.primary.main}`,
        borderRadius: 1,
        p: 2,
        mt: 2,
        bgcolor: 'rgba(244, 67, 54, 0.04)',
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        mb={1}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <MenuBookIcon color='primary' />
          <Typography variant='h6'>Mitlese-Sitzung läuft</Typography>
          <Tooltip
            title={
              session.ControlMode === 'push'
                ? 'Aktuell: Notar pusht die Seite. Klicken zum Wechsel auf Freie Steuerung durch Tablets.'
                : 'Aktuell: Tablets blättern selbst. Klicken zum Wechsel auf Notar-führt.'
            }
          >
            <Chip
              label={
                session.ControlMode === 'push' ? 'Notar führt' : 'Tablets frei'
              }
              color='primary'
              size='small'
              onClick={toggleControlMode}
              clickable
              disabled={isPendingUpdateReadingSession}
            />
          </Tooltip>
        </Stack>
        <Button
          color='error'
          startIcon={<StopCircleIcon />}
          onClick={handleEnd}
          disabled={isPendingEndReadingSession}
        >
          Beenden
        </Button>
      </Stack>

      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Dokument: <strong>{session.DocumentFileName ?? '–'}</strong>
      </Typography>

      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
        <Tooltip title='Eine Seite zurück'>
          <span>
            <IconButton
              onClick={() => setPage((session.CurrentPage ?? 1) - 1)}
              disabled={
                isPendingUpdateReadingSession || (session.CurrentPage ?? 1) <= 1
              }
            >
              <NavigateBeforeIcon fontSize='large' />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          size='small'
          label='Seite'
          value={pageInput}
          onChange={e => setPageInput(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={() => {
            const n = Math.max(1, Number(pageInput) || 1);
            if (n !== session.CurrentPage) setPage(n);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              const n = Math.max(1, Number(pageInput) || 1);
              if (n !== session.CurrentPage) setPage(n);
            }
          }}
          inputProps={{
            inputMode: 'numeric',
            style: { textAlign: 'center', width: 60 },
          }}
        />
        <Tooltip title='Eine Seite vor'>
          <span>
            <IconButton
              onClick={() => setPage((session.CurrentPage ?? 1) + 1)}
              disabled={isPendingUpdateReadingSession}
            >
              <NavigateNextIcon fontSize='large' />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant='caption' color='text.secondary' sx={{ ml: 1 }}>
          Tablets folgen automatisch.
        </Typography>
      </Stack>

      <Typography variant='subtitle2' gutterBottom>
        Teilnehmende Tablets ({devices.length})
      </Typography>
      <Stack direction='row' spacing={1} flexWrap='wrap' sx={{ gap: 1 }}>
        {devices.map(d => {
          const online = isOnline(d.LastSeen_at);
          const isOnRightPage = (d.CurrentPage ?? 1) === session.CurrentPage;
          return (
            <Chip
              key={d.idTabletReadingSessionDevice}
              label={`${d.TabletName ?? '?'} · S. ${d.CurrentPage ?? '–'}`}
              size='small'
              color={
                online ? (isOnRightPage ? 'success' : 'warning') : 'default'
              }
              variant={online ? 'filled' : 'outlined'}
            />
          );
        })}
      </Stack>

      {devices.some(d => !isOnline(d.LastSeen_at)) && (
        <Alert severity='warning' sx={{ mt: 2 }}>
          Ein oder mehrere Tablets melden sich gerade nicht. Bitte prüfen, ob
          die Mitlese-App offen ist.
        </Alert>
      )}

      <Stack direction='row' alignItems='center' spacing={1} sx={{ mt: 2 }}>
        <Typography variant='subtitle2'>Dokument (Notar-Ansicht)</Typography>
        <Tooltip
          title={showDocument ? 'Dokument ausblenden' : 'Dokument einblenden'}
        >
          <IconButton size='small' onClick={() => setShowDocument(v => !v)}>
            {showDocument ? (
              <VisibilityOffIcon fontSize='small' />
            ) : (
              <VisibilityIcon fontSize='small' />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
      <Collapse in={showDocument}>
        <Box
          sx={{
            mt: 1,
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {pdfLoading ? (
            <Stack alignItems='center' justifyContent='center' sx={{ py: 4 }}>
              <CircularProgress size={32} />
            </Stack>
          ) : pdfUrl ? (
            <PdfDocument
              pdfFile={pdfUrl}
              numPages={numPages}
              pageNumber={session.CurrentPage ?? 1}
              setNumPages={setNumPages}
              scale={1.0}
              scrollable={false}
            />
          ) : (
            <Alert severity='error'>
              Dokument konnte nicht geladen werden.
            </Alert>
          )}
        </Box>
      </Collapse>

      {(annotations ?? []).length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Stack direction='row' spacing={1} alignItems='center' mb={1}>
            <ChatBubbleOutlineIcon fontSize='small' color='primary' />
            <Typography variant='subtitle2'>
              Anmerkungen ({(annotations ?? []).length})
            </Typography>
          </Stack>
          <Box
            sx={{
              maxHeight: 280,
              overflow: 'auto',
              border: theme => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1,
            }}
          >
            {(annotations ?? []).map(a => {
              const author =
                [a.LegalClientFirstName, a.LegalClientLastName]
                  .filter(Boolean)
                  .join(' ') ||
                a.TabletName ||
                `Tablet #${a.TabletDevice_idTabletDevice}`;
              return (
                <Box
                  key={a.idTabletReadingAnnotation}
                  sx={{
                    py: 0.75,
                    px: 1,
                    borderLeft: theme =>
                      `3px solid ${theme.palette.primary.light}`,
                    mb: 0.75,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack direction='row' spacing={1} alignItems='baseline'>
                    <Typography variant='caption' color='primary.main'>
                      S. {a.Page}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {author}
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.disabled'
                      sx={{ ml: 'auto' }}
                    >
                      {a.Created_at
                        ? new Date(a.Created_at).toLocaleTimeString('de-DE')
                        : ''}
                    </Typography>
                  </Stack>
                  <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                    {a.Text}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};
